import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetProfile, apiGetChatHistory, apiSendMessage, apiCloneVoice, apiTextToSpeech } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, Mic2, Volume2, LayoutDashboard } from "lucide-react";
import { motion } from "framer-motion";

export default function ChatPage() {
  const { profileId } = useParams();
  const { token } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingChat, setLoadingChat] = useState(true);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [cloningVoice, setCloningVoice] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!token || !profileId) return;
    Promise.all([
      apiGetProfile(token, profileId),
      apiGetChatHistory(token, profileId),
    ]).then(([p, msgs]) => {
      setProfile(p);
      setMessages(msgs);
    }).catch((err) => {
      toast.error(err.message);
      nav("/dashboard");
    }).finally(() => setLoadingChat(false));
  }, [token, profileId, nav]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    const tempUserMsg = { id: `temp-${Date.now()}`, role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, tempUserMsg]);
    try {
      const aiMsg = await apiSendMessage(token, profileId, text);
      setMessages((prev) => [...prev, aiMsg]);
    } catch {
      toast.error("Errore nell'invio del messaggio");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleCloneVoice = async () => {
    if (cloningVoice) return;
    setCloningVoice(true);
    try {
      await apiCloneVoice(token, profileId);
      const p = await apiGetProfile(token, profileId);
      setProfile(p);
      toast.success("Voce clonata con successo!");
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("missing_permissions") || msg.includes("permission")) {
        toast.error("La chiave ElevenLabs non ha il permesso di clonazione. Attivalo dalla dashboard ElevenLabs.");
      } else {
        toast.error(msg || "Errore clonazione voce");
      }
    } finally {
      setCloningVoice(false);
    }
  };

  const playVoice = async (text, msgId) => {
    if (playingAudio === msgId) {
      audioRef.current?.pause();
      setPlayingAudio(null);
      return;
    }
    try {
      setPlayingAudio(msgId);
      const res = await apiTextToSpeech(token, profileId, text);
      const audio = new Audio(res.audio_url);
      audioRef.current = audio;
      audio.onended = () => setPlayingAudio(null);
      audio.onerror = () => { setPlayingAudio(null); toast.error("Errore riproduzione"); };
      audio.play();
    } catch {
      toast.error("Errore riproduzione voce");
      setPlayingAudio(null);
    }
  };

  if (loadingChat) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs text-muted-foreground">Caricamento conversazione...</p>
        </div>
      </div>
    );
  }

  const displayName = profile?.soprannome || profile?.nome || "...";

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="chat-page">
      {/* Header */}
      <div className="border-b border-border/40 bg-primary px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <Button variant="ghost" size="icon" onClick={() => nav("/dashboard")}
          className="text-primary-foreground hover:bg-primary-foreground/10 h-9 w-9" data-testid="chat-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        {profile?.photos?.length > 0 ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${profile.photos[0]}`} alt=""
            className="w-9 h-9 rounded-full object-cover ring-2 ring-primary-foreground/15" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-primary-foreground/15 flex items-center justify-center">
            <span className="text-sm font-bold text-primary-foreground">{profile?.nome?.[0]}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-primary-foreground truncate">{displayName}</p>
          <p className="text-[11px] text-primary-foreground/50 capitalize">{profile?.relazione}</p>
        </div>
        <div className="flex items-center gap-1">
          {profile?.voice_id ? (
            <span className="text-[10px] text-primary-foreground/40 flex items-center gap-1 mr-1">
              <Volume2 className="w-3 h-3" /> Voce
            </span>
          ) : profile?.audio_files?.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={handleCloneVoice} disabled={cloningVoice}
              className="text-primary-foreground/60 hover:text-primary-foreground hover:bg-primary-foreground/10 text-[11px] h-8"
              data-testid="clone-voice-btn">
              {cloningVoice ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Mic2 className="w-3 h-3 mr-1" />}
              {cloningVoice ? "Clonazione..." : "Clona voce"}
            </Button>
          ) : null}
          <Button variant="ghost" size="icon" onClick={() => nav("/dashboard")}
            className="text-primary-foreground/40 hover:text-primary-foreground hover:bg-primary-foreground/10 h-8 w-8"
            data-testid="chat-dashboard-btn">
            <LayoutDashboard className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "hsl(var(--warm-sand))" }}>
        {messages.length === 0 && (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-2xl bg-accent/8 flex items-center justify-center mx-auto mb-4">
              <MessageBubbleIcon />
            </div>
            <p className="text-sm text-muted-foreground font-medium">Inizia una conversazione con {displayName}</p>
            <p className="text-xs text-muted-foreground/50 mt-1">Scrivi qualcosa qui sotto per cominciare</p>
          </div>
        )}
        {messages.map((msg) => (
          <motion.div
            key={msg.id}
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.2 }}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div className={`max-w-[80%] sm:max-w-[65%] rounded-2xl px-4 py-2.5 ${
              msg.role === "user"
                ? "bg-primary text-primary-foreground rounded-br-md"
                : "bg-card text-foreground shadow-soft rounded-bl-md"
            }`}>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              <div className={`flex items-center gap-2 mt-1 ${msg.role === "user" ? "justify-end" : ""}`}>
                {msg.role === "assistant" && profile?.voice_id && (
                  <button onClick={() => playVoice(msg.content, msg.id)}
                    className="text-muted-foreground hover:text-accent transition-colors"
                    data-testid={`play-voice-${msg.id}`}>
                    <Volume2 className={`w-3.5 h-3.5 ${playingAudio === msg.id ? "text-accent animate-pulse" : ""}`} />
                  </button>
                )}
                <p className={`text-[10px] ${msg.role === "user" ? "text-primary-foreground/40" : "text-muted-foreground/60"}`}>
                  {new Date(msg.timestamp).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
        {sending && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
            <div className="bg-card shadow-soft rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </motion.div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-border/40 bg-card p-3 flex items-center gap-2 flex-shrink-0">
        <Input
          ref={inputRef}
          placeholder={`Scrivi a ${displayName}...`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
          className="flex-1 border-0 bg-secondary text-sm focus-visible:ring-0 rounded-full px-4 h-10"
          disabled={sending}
          data-testid="chat-input"
        />
        <Button variant="default" size="icon" onClick={handleSend} disabled={sending || !input.trim()}
          className="rounded-full w-10 h-10 flex-shrink-0" data-testid="chat-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

function MessageBubbleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-accent/40">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
